import PropTypes from "prop-types";
import { useState } from "react";
import { Card, Divider } from "@mantine/core";
import CustomSelect from "../../../ui/CustomSelect/CustomSelect";
import { Controller, useWatch } from "react-hook-form";
import {
  HiOutlineUserPlus,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineExclamationCircle,
  HiOutlineLockClosed,
} from "react-icons/hi2";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import CustomTextInput from "../../../ui/CustomTextInput/CustomTextInput";
import CustomNumberInput from "../../../ui/CustomNumberInput/CustomNumberInput";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomInlineBanner from "../../../ui/CustomInlineBanner/CustomInlineBanner";
import CustomLoader from "../../../ui/CustomLoader/CustomLoader";
import {
  entryLimitBannerStyle,
  channelLockBannerStyle,
} from "../styles/manualEntryStyles";
import { exceedsBalance, formatKES } from "../../../../utils/transactionutils";
import useManualEntryStore from "../../../../stores/manualEntryStore";
import {
  MANUAL_ENTRY_CHANNEL_OPTIONS,
  MANUAL_ENTRY_CHANNELS,
  MAX_MANUAL_ENTRIES,
} from "../config/manualEntryConfig";
import FetchFeedback from "../../../ui/CustomFetchFeedback/CustomFetchFeedback";
import {
  MIN_AMOUNT_BANK,
  REMARKS_MAX_BANK,
  REMARKS_MAX_MPESA,
} from "../../validations/recipientValidators";

function getRemainingLimitColor(disbursementLimits, isLimitExceeded) {
  if (!disbursementLimits?.dailyLimit || disbursementLimits.availableDailyLimit == null)
    return "text-gray-900";
  if (isLimitExceeded) return "text-red-600";
  const ratio = disbursementLimits.availableDailyLimit / disbursementLimits.dailyLimit;
  if (ratio < 0.25) return "text-red-600";
  if (ratio < 0.5) return "text-amber-600";
  return "text-green-600";
}

RecipientCard.propTypes = {
  control: PropTypes.object.isRequired,
  validators: PropTypes.object.isRequired,
  onAddToList: PropTypes.func.isRequired,
  walletBalance: PropTypes.number,
  totalPlusFees: PropTypes.number,
  editingEntryTotalCost: PropTypes.number,
  isSubmitting: PropTypes.bool,
  isAtLimit: PropTypes.bool,
  isEditing: PropTypes.bool,
  onCancelEdit: PropTypes.func,
  isChannelLocked: PropTypes.bool,
  paymentChannel: PropTypes.string,
  handlePhoneChange: PropTypes.func,
  handlePhoneBlur: PropTypes.func,
  isVerifying: PropTypes.bool,
  isVerified: PropTypes.bool,
  isUnverified: PropTypes.bool,
  handleAccountNumberChange: PropTypes.func.isRequired,
  handleAccountNumberBlur: PropTypes.func.isRequired,
  isBankVerifying: PropTypes.bool,
  isBankVerified: PropTypes.bool,
  isBankUnverified: PropTypes.bool,
  banks: PropTypes.array,
  bankOptions: PropTypes.array,
  bankFilter: PropTypes.func,
  isBanksLoading: PropTypes.bool,
  isBanksError: PropTypes.bool,
  onRetryBanks: PropTypes.func,
  isFormValid: PropTypes.bool,
  disbursementLimits: PropTypes.shape({
    minAmount: PropTypes.number,
    maxAmount: PropTypes.number,
    dailyLimit: PropTypes.number,
    availableDailyLimit: PropTypes.number,
    monthlyLimit: PropTypes.number,
    availableMonthlyLimit: PropTypes.number,
  }),
  isLimitsLoading: PropTypes.bool,
  isLimitExceeded: PropTypes.bool,
};

export default function RecipientCard({
  control,
  validators,
  onAddToList,
  walletBalance = null,
  totalPlusFees = 0,
  editingEntryTotalCost = 0,
  isSubmitting,
  isAtLimit = false,
  isEditing = false,
  onCancelEdit,
  isChannelLocked = false,
  paymentChannel,
  handlePhoneChange = () => {},
  handlePhoneBlur = () => {},
  isVerifying = false,
  isVerified = false,
  isUnverified = false,
  handleAccountNumberChange,
  handleAccountNumberBlur,
  isBankVerifying = false,
  isBankVerified = false,
  isBankUnverified = false,
  banks = [],
  bankOptions = [],
  bankFilter,
  isBanksLoading = false,
  isBanksError = false,
  onRetryBanks,
  isFormValid = false,
  disbursementLimits = null,
  isLimitsLoading = false,
  isLimitExceeded = false,
}) {
  const [isLockBannerDismissed, setIsLockBannerDismissed] = useState(false);

  const phoneVerificationMessage = useManualEntryStore(
    (s) => s.phoneVerificationMessage,
  );
  const bankVerificationMessage = useManualEntryStore(
    (s) => s.bankVerificationMessage,
  );

  const [selectedChannel, watchedBankCode] = useWatch({
    control,
    name: ["channel", "bankCode"],
  });
  const isBank = selectedChannel === MANUAL_ENTRY_CHANNELS.BANK;
  const hasBankSelected = isBank && !!watchedBankCode;

  const watchedAmount = useWatch({ control, name: "amount" });
  const committedAmount = isEditing
    ? totalPlusFees - editingEntryTotalCost
    : totalPlusFees;
  const isBalanceInsufficient =
    walletBalance !== null &&
    exceedsBalance(Number(watchedAmount) + committedAmount, walletBalance);
  const isDisabled = isSubmitting || (isAtLimit && !isEditing);

  const isVerificationPassed = isBank ? isBankVerified : isVerified;
  const isAddDisabled =
    isDisabled ||
    isBalanceInsufficient ||
    isLimitExceeded ||
    !isVerificationPassed ||
    !isFormValid;

  const getTooltipLabel = () => {
    if (isBank && !isBankVerified) return "Verify the bank account to continue";
    if (!isBank && !isVerified)
      return "Verify the recipient's phone number to continue";
    if (isBalanceInsufficient) return "Batch total exceeds your wallet balance";
    if (isLimitExceeded) return "Amount exceeds your available daily limit";
    return undefined;
  };

  const remainingLimitColor = getRemainingLimitColor(disbursementLimits, isLimitExceeded);

  return (
    <Card radius="lg" className="p-5 sm:p-6 w-full md:flex-1 md:min-w-[480px]">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-primary-200">
          <HiOutlineUserPlus className="text-lg text-primary-800" />
        </div>
        <span className="font-semibold text-gray-900">
          Recipient Information
        </span>
      </div>

      {isChannelLocked && !isLockBannerDismissed && (
        <CustomInlineBanner
          styles={channelLockBannerStyle}
          icon={<HiOutlineLockClosed size={14} />}
          withCloseButton
          onClose={() => setIsLockBannerDismissed(true)}
          className="mb-5"
        >
          <p className="text-xs text-primary-600">
            Channel locked to <strong>{paymentChannel}</strong>. Delete all
            entries to switch channels.
          </p>
        </CustomInlineBanner>
      )}

      {isAtLimit && !isEditing && (
        <CustomInlineBanner
          styles={entryLimitBannerStyle}
          icon={<HiOutlineExclamationCircle size={14} />}
          className="mb-5"
        >
          <p className="text-xs text-red-700">
            Maximum {MAX_MANUAL_ENTRIES} entries reached. Remove an entry to add
            more, or use File Upload to submit a larger batch.
          </p>
        </CustomInlineBanner>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 rounded-lg px-3 py-1.5 mb-5">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm text-gray-500">
            Institutional Daily Limit:{" "}
            {isLimitsLoading ? (
              <span className="text-gray-400">Loading…</span>
            ) : disbursementLimits ? (
              <span className="text-gray-900 font-medium">
                {formatKES(disbursementLimits.dailyLimit)}
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
          <span className="text-sm text-gray-500">
            Today's Remaining Limit:{" "}
            {isLimitsLoading ? (
              <span className="text-gray-400">Loading…</span>
            ) : disbursementLimits ? (
              <span className={`${remainingLimitColor} font-medium`}>
                {formatKES(disbursementLimits.availableDailyLimit)}
              </span>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </span>
        </div>
        <Controller
          name="channel"
          control={control}
          rules={{ validate: (v) => validators.channel(v) ?? true }}
          render={({ field, fieldState }) => (
            <CustomSelect
              placeholder="Select Channel"
              data={MANUAL_ENTRY_CHANNEL_OPTIONS}
              value={field.value || null}
              onChange={field.onChange}
              error={fieldState.error?.message}
              size="sm"
              radius="md"
              w={160}
              disabled={isDisabled || isChannelLocked}
              comboboxProps={{ withinPortal: false }}
            />
          )}
        />
      </div>

      <div className="flex flex-col gap-4">
        {/* ── Mpesa fields ── */}
        {!isBank && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="phoneNumber"
              control={control}
              rules={{ validate: (v) => validators.phoneNumber(v) ?? true }}
              render={({ field, fieldState }) => (
                <div className="space-y-1">
                  <CustomTextInput
                    label="Phone Number"
                    placeholder="254700000000"
                    radius="md"
                    withAsterisk
                    disabled={isDisabled}
                    rightSection={
                      isVerifying ? (
                        <CustomLoader size={14} color="success" />
                      ) : null
                    }
                    value={field.value}
                    onChange={(e) => {
                      const value = e.target.value
                        .replaceAll(/\D/g, "")
                        .slice(0, 12);
                      field.onChange(value);
                      handlePhoneChange(value);
                    }}
                    onBlur={() => {
                      field.onBlur();
                      handlePhoneBlur(field.value);
                    }}
                    error={
                      fieldState.error?.message ||
                      (isUnverified ? true : undefined)
                    }
                  />
                  {isVerified && (
                    <div className="flex items-center gap-1.5 px-1">
                      <FaCheckCircle
                        style={{ fontSize: 13 }}
                        className="text-green-500 shrink-0"
                      />
                      <span className="text-xs text-green-600 font-medium">
                        Recipient verified
                      </span>
                    </div>
                  )}
                  {isUnverified && (
                    <div className="flex items-center gap-1.5 px-1">
                      <FaTimesCircle
                        style={{ fontSize: 13 }}
                        className="text-red-500 shrink-0"
                      />
                      <span className="text-xs text-red-500 font-medium">
                        {phoneVerificationMessage ||
                          "Could not verify recipient"}
                      </span>
                    </div>
                  )}
                </div>
              )}
            />

            <Controller
              name="recipientName"
              control={control}
              rules={{ validate: (v) => validators.recipientName(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label="Recipient Name"
                  placeholder="Auto-populated after verification"
                  radius="md"
                  withAsterisk
                  disabled
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        )}

        {/* ── Bank fields ── */}
        {isBank && (
          <div className="flex flex-col gap-4">
            <Controller
              name="bankCode"
              control={control}
              rules={{ validate: (v) => validators.bankCode(v) ?? true }}
              render={({ field, fieldState }) => {
                const selectedBankId =
                  banks.find((b) => b.code === field.value)?.id?.toString() ??
                  null;
                return (
                  <CustomSelect
                    label="Bank"
                    placeholder={
                      isBanksLoading
                        ? "Loading banks..."
                        : "Search by name or code"
                    }
                    data={bankOptions}
                    searchable
                    lockOnSelect
                    filter={bankFilter}
                    value={selectedBankId}
                    onChange={(bankId) => {
                      const bank = banks.find(
                        (b) => b.id.toString() === bankId,
                      );
                      field.onChange(bank?.code ?? "");
                    }}
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                    withAsterisk
                    radius="md"
                    disabled={isDisabled || isBanksLoading || isBanksError}
                    comboboxProps={{ withinPortal: false }}
                  />
                );
              }}
            />

            {isBanksError && (
              <FetchFeedback
                message="Failed to load banks. Please try again."
                onRetry={onRetryBanks}
                fullHeight={false}
                compact
              />
            )}

            {hasBankSelected && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name="accountNumber"
                  control={control}
                  rules={{
                    validate: (v) => validators.accountNumber(v) ?? true,
                  }}
                  render={({ field, fieldState }) => (
                    <div className="space-y-1">
                      <CustomTextInput
                        label="Account Number"
                        placeholder="Enter account number"
                        radius="md"
                        withAsterisk
                        disabled={isDisabled}
                        rightSection={
                          isBankVerifying ? (
                            <CustomLoader size={14} color="success" />
                          ) : null
                        }
                        value={field.value}
                        onChange={(e) => {
                          const value = e.target.value
                            .replaceAll(/\D/g, "")
                            .slice(0, 16);
                          field.onChange(value);
                          handleAccountNumberChange(value);
                        }}
                        onBlur={() => {
                          field.onBlur();
                          handleAccountNumberBlur(field.value);
                        }}
                        error={
                          fieldState.error?.message ||
                          (isBankUnverified ? true : undefined)
                        }
                      />
                      {isBankVerified && (
                        <div className="flex items-center gap-1.5 px-1">
                          <FaCheckCircle
                            style={{ fontSize: 13 }}
                            className="text-green-500 shrink-0"
                          />
                          <span className="text-xs text-green-600 font-medium">
                            Account verified
                          </span>
                        </div>
                      )}
                      {isBankUnverified && (
                        <div className="flex items-center gap-1.5 px-1">
                          <FaTimesCircle
                            style={{ fontSize: 13 }}
                            className="text-red-500 shrink-0"
                          />
                          <span className="text-xs text-red-500 font-medium">
                            {bankVerificationMessage ||
                              "Could not verify account"}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                />

                <Controller
                  name="recipientName"
                  control={control}
                  rules={{
                    validate: (v) => validators.recipientName(v) ?? true,
                  }}
                  render={({ field, fieldState }) => (
                    <CustomTextInput
                      label="Recipient Name"
                      placeholder="Auto-populated after verification"
                      radius="md"
                      withAsterisk
                      disabled
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            )}
          </div>
        )}

        <Controller
          name="amount"
          control={control}
          rules={{
            validate: {
              format: (v) =>
                validators.amount(v, isBank, disbursementLimits) ?? true,
              balance: (v) =>
                walletBalance !== null &&
                exceedsBalance(Number(v) + committedAmount, walletBalance)
                  ? "Batch total exceeds your wallet balance."
                  : true,
              dailyLimit: () =>
                isLimitExceeded
                  ? `Batch total exceeds your available daily limit of ${formatKES(disbursementLimits?.availableDailyLimit)}.`
                  : true,
            },
          }}
          render={({ field, fieldState }) => (
            <div className="space-y-1">
              <CustomNumberInput
                label="Amount (KES)"
                placeholder="Enter amount"
                radius="md"
                withAsterisk
                min={disbursementLimits?.minAmount ?? MIN_AMOUNT_BANK}
                clampBehavior="none"
                thousandSeparator=","
                disabled={isDisabled}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
              {disbursementLimits && (
                <p className="text-xs text-gray-400 px-1">
                  Min: {formatKES(disbursementLimits.minAmount)} · Max:{" "}
                  {formatKES(disbursementLimits.maxAmount)}
                </p>
              )}
            </div>
          )}
        />

        <Controller
          name="remarks"
          control={control}
          rules={{
            validate: (v) => validators.remarks(v, isBank) ?? true,
          }}
          render={({ field, fieldState }) => {
            const remarksMaxLength = isBank
              ? REMARKS_MAX_BANK
              : REMARKS_MAX_MPESA;
            return (
              <CustomTextInput
                label="Reason / Remarks"
                placeholder="Enter reason for payment"
                radius="md"
                withAsterisk
                disabled={isDisabled}
                value={field.value}
                onChange={(e) =>
                  field.onChange(e.target.value.slice(0, remarksMaxLength))
                }
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            );
          }}
        />
      </div>

      <Divider my="lg" />

      <div className="flex justify-end gap-2">
        {isEditing && (
          <CustomButton
            radius="md"
            variant="default"
            onClick={onCancelEdit}
            disabled={isSubmitting}
          >
            Cancel
          </CustomButton>
        )}
        <CustomButton
          radius="md"
          leftSection={
            isEditing ? (
              <HiOutlinePencil className="text-base" />
            ) : (
              <HiOutlinePlus className="text-base" />
            )
          }
          onClick={onAddToList}
          loading={isSubmitting}
          disabled={isAddDisabled}
          title={getTooltipLabel()}
        >
          {isEditing ? "Update Entry" : "Add To List"}
        </CustomButton>
      </div>
    </Card>
  );
}
